// v12
// Path: frontend/src/components/QuickBooksSync/StandardMappingsModal.jsx
// Inputs: token, onClose, onSaved
// Outputs: React JSX modal for managing multi-set standard field mapping presets across source and target systems
// Summary: Interface for creating, viewing, configuring, and cloning standard field mapping presets organized by named sets (SetName) and source/target system pairs, utilizing the unified FieldMappingDesigner with sourceSystem propagation.

import React, { useState, useEffect, useMemo } from 'react';
import { FieldMappingDesigner } from './FieldMappingDesigner';
import { QUICKBOOKS_TARGET_ENTITIES, getQbEntityMeta, normalizeEntityName } from './quickbooksEntities';

export const StandardMappingsModal = ({ token, onClose, onSaved }) => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [mappings, setMappings] = useState([]);
    const [availableSets, setAvailableSets] = useState(['Default']);
    const [selectedSet, setSelectedSet] = useState('Default');
    const [sourceSystems, setSourceSystems] = useState(['Passport', 'QuickBooks', 'Xero']);
    const [selectedSystem, setSelectedSystem] = useState('Passport');
    const [targetSystems, setTargetSystems] = useState(['QuickBooks', 'Xero', 'Passport']);
    const [selectedTargetSystem, setSelectedTargetSystem] = useState('QuickBooks');
    const [selectedEntityType, setSelectedEntityType] = useState('Customer');
    const [selectedMapping, setSelectedMapping] = useState(null);
    const [defaultConnPrefix, setDefaultConnPrefix] = useState('');
    const [defaultDbName, setDefaultDbName] = useState('pbsdata00');
    const [message, setMessage] = useState(null);

    // New Set Modal State
    const [showNewSetModal, setShowNewSetModal] = useState(false);
    const [newSetName, setNewSetName] = useState('');
    const [cloneFromSet, setCloneFromSet] = useState('Default');

    const canonicalEntities = QUICKBOOKS_TARGET_ENTITIES.map(e => e.entity);

    const fetchMappings = async (activeSet = selectedSet, srcSys = selectedSystem, tgtSys = selectedTargetSystem) => {
        setLoading(true);
        try {
            const res = await fetch(
                `/api/quickbooks/standard-mappings?sourceSystem=${encodeURIComponent(srcSys)}&targetSystem=${encodeURIComponent(tgtSys)}&setName=${encodeURIComponent(activeSet)}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (res.ok) {
                const data = await res.json();
                const fetched = Array.isArray(data.mappings) ? data.mappings : [];
                setMappings(fetched);
                if (Array.isArray(data.availableSets) && data.availableSets.length > 0) {
                    setAvailableSets(data.availableSets);
                }
                if (Array.isArray(data.availableSourceSystems) && data.availableSourceSystems.length > 0) {
                    setSourceSystems(data.availableSourceSystems);
                }
                if (Array.isArray(data.availableTargetSystems) && data.availableTargetSystems.length > 0) {
                    setTargetSystems(data.availableTargetSystems);
                }
                if (data.defaultSourceConnPrefix) setDefaultConnPrefix(data.defaultSourceConnPrefix);
                if (data.defaultSourceDatabaseName) setDefaultDbName(data.defaultSourceDatabaseName);

                // Match currently selected entity
                const matched = fetched.find(m => {
                    const norm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
                    return norm.toLowerCase() === selectedEntityType.toLowerCase();
                });

                if (matched) {
                    const canonical = normalizeEntityName(matched.TargetQBEntity || matched.EntityType);
                    setSelectedEntityType(canonical);
                    setSelectedMapping(matched);
                } else {
                    const meta = getQbEntityMeta(selectedEntityType);
                    setSelectedMapping({
                        MappingID: null,
                        SetName: activeSet,
                        SourceSystem: srcSys,
                        TargetSystem: tgtSys,
                        EntityType: selectedEntityType,
                        TargetQBEntity: selectedEntityType,
                        SourceTable: meta.defaultTable || '',
                        MappingName: `${activeSet} - ${srcSys} ${selectedEntityType} -> ${tgtSys}`,
                        Description: `Preset field mappings for ${selectedEntityType} in ${activeSet} set`,
                        IsDefault: true,
                        mappings: []
                    });
                }
            }
        } catch (err) {
            console.error('[StandardMappingsModal] Error fetching mappings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
    }, [selectedSet, selectedSystem, selectedTargetSystem]);

    const handleSelectEntity = (ent) => {
        const canonical = normalizeEntityName(ent);
        setSelectedEntityType(canonical);
        const matched = mappings.find(m => {
            const mNorm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
            return mNorm.toLowerCase() === canonical.toLowerCase();
        });
        const meta = getQbEntityMeta(canonical);
        setSelectedMapping(matched || {
            MappingID: null,
            SetName: selectedSet,
            SourceSystem: selectedSystem,
            TargetSystem: selectedTargetSystem,
            EntityType: canonical,
            TargetQBEntity: canonical,
            SourceTable: meta.defaultTable || '',
            MappingName: `${selectedSet} - ${selectedSystem} ${canonical} -> ${selectedTargetSystem}`,
            Description: `Preset field mappings for ${canonical} in ${selectedSet} set`,
            IsDefault: true,
            mappings: []
        });
        setMessage(null);
    };

    const handleCreateNewSet = async (e) => {
        if (e) e.preventDefault();
        const trimmed = newSetName.trim();
        if (!trimmed) {
            alert('Please enter a name for the new standard set.');
            return;
        }
        setSaving(true);
        try {
            const res = await fetch('/api/quickbooks/standard-sets', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    newSetName: trimmed,
                    cloneFromSetName: cloneFromSet === '__NONE__' ? undefined : cloneFromSet,
                    sourceSystem: selectedSystem,
                    targetSystem: selectedTargetSystem
                })
            });
            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: `Standard Set "${trimmed}" created successfully!` });
                setShowNewSetModal(false);
                setNewSetName('');
                if (!availableSets.includes(trimmed)) {
                    setAvailableSets(prev => [...prev, trimmed]);
                }
                setSelectedSet(trimmed);
                await fetchMappings(trimmed, selectedSystem, selectedTargetSystem);
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to create standard set.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleSavePreset = async (designerPayload) => {
        setSaving(true);
        setMessage(null);
        try {
            const current = selectedMapping || {};
            const payload = {
                mappingId: current.MappingID || undefined,
                setName: selectedSet,
                sourceSystem: current.SourceSystem || selectedSystem,
                targetSystem: current.TargetSystem || selectedTargetSystem,
                entityType: selectedEntityType,
                targetQBEntity: selectedEntityType,
                sourceTable: designerPayload.sourceTable || current.SourceTable || '',
                mappingName: current.MappingName || `${selectedSet} - ${selectedSystem} ${selectedEntityType} -> ${selectedTargetSystem}`,
                description: current.Description || '',
                isDefault: current.IsDefault !== undefined ? current.IsDefault : true,
                mappings: designerPayload.mappings
            };

            const res = await fetch('/api/quickbooks/standard-mappings', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(payload)
            });

            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: data.message || 'Standard mapping preset saved!' });
                await fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to save standard mapping.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const handleResetDefaults = async () => {
        if (!selectedMapping?.MappingID) {
            setMessage({ type: 'info', text: 'Preset is already blank for this entity.' });
            return;
        }
        if (!window.confirm(`Clear the saved standard mapping for ${selectedEntityType} in set "${selectedSet}"?`)) return;
        setSaving(true);
        try {
            const res = await fetch(`/api/quickbooks/standard-mappings/${selectedMapping.MappingID}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (res.ok) {
                setMessage({ type: 'success', text: `Standard mapping for ${selectedEntityType} cleared.` });
                await fetchMappings(selectedSet, selectedSystem, selectedTargetSystem);
                if (onSaved) onSaved();
            } else {
                setMessage({ type: 'error', text: data.error || 'Failed to clear mapping.' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: err.message });
        } finally {
            setSaving(false);
        }
    };

    const meta = getQbEntityMeta(selectedEntityType);

    const activeMappingProp = useMemo(() => {
        const base = selectedMapping || {};
        return {
            MappingID: base.MappingID || null,
            SetName: base.SetName || selectedSet,
            SourceSystem: base.SourceSystem || selectedSystem,
            TargetSystem: base.TargetSystem || selectedTargetSystem,
            EntityType: base.EntityType || selectedEntityType,
            TargetQBEntity: base.TargetQBEntity || selectedEntityType,
            SourceTable: base.SourceTable || meta.defaultTable || '',
            SourceConnPrefix: defaultConnPrefix,
            SourceDatabaseName: defaultDbName,
            MappingName: base.MappingName || `${selectedSet} - ${selectedSystem} ${selectedEntityType} -> ${selectedTargetSystem}`,
            Description: base.Description || `Preset mappings for ${selectedEntityType} in ${selectedSet} set`,
            IsDefault: base.IsDefault !== undefined ? base.IsDefault : true,
            FieldMappingsJson: base.FieldMappingsJson || (Array.isArray(base.mappings) ? JSON.stringify(base.mappings) : '[]'),
            mappings: Array.isArray(base.mappings) ? base.mappings : []
        };
    }, [selectedMapping, selectedSet, selectedSystem, selectedTargetSystem, selectedEntityType, meta.defaultTable, defaultConnPrefix, defaultDbName]);

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-6xl shadow-2xl flex flex-col h-[92vh] overflow-hidden">
                {/* System & Set Navigation Bar */}
                <div className="px-6 py-3 bg-slate-850 border-b border-slate-800 flex flex-wrap items-center justify-between gap-4 shrink-0">
                    <div className="flex items-center gap-3 flex-wrap">
                        {/* Standard Set Selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                <span className="material-symbols-outlined text-[15px] text-amber-400">bookmark</span>
                                Standard Set:
                            </span>
                            <select
                                value={selectedSet}
                                onChange={e => setSelectedSet(e.target.value)}
                                className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-1 text-xs text-amber-300 font-bold focus:border-amber-500 focus:outline-none"
                            >
                                {availableSets.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => { setNewSetName(''); setCloneFromSet(selectedSet); setShowNewSetModal(true); }}
                                className="px-2 py-1 bg-slate-800 hover:bg-slate-700 text-amber-300 border border-slate-700 rounded text-xs font-semibold flex items-center gap-1 transition-colors"
                                title="Create or clone a new Standard Set"
                            >
                                <span className="material-symbols-outlined text-[14px]">add</span>
                                New Set
                            </button>
                        </div>

                        <span className="text-slate-600">|</span>

                        {/* Source System Selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                <span className="material-symbols-outlined text-[15px] text-indigo-400">device_hub</span>
                                Source:
                            </span>
                            <select
                                value={selectedSystem}
                                onChange={e => setSelectedSystem(e.target.value)}
                                className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-white font-semibold focus:border-indigo-500 focus:outline-none"
                            >
                                {sourceSystems.map(sys => (
                                    <option key={sys} value={sys}>{sys}</option>
                                ))}
                            </select>
                        </div>

                        <span className="text-slate-500 text-xs">&rarr;</span>

                        {/* Target System Selector */}
                        <div className="flex items-center gap-2">
                            <span className="text-xs font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                                <span className="material-symbols-outlined text-[15px] text-emerald-400">cloud</span>
                                Target:
                            </span>
                            <select
                                value={selectedTargetSystem}
                                onChange={e => setSelectedTargetSystem(e.target.value)}
                                className="bg-slate-900 border border-slate-700 rounded-lg px-2.5 py-1 text-xs text-emerald-300 font-semibold focus:border-emerald-500 focus:outline-none"
                            >
                                {targetSystems.map(tgt => (
                                    <option key={tgt} value={tgt}>{tgt}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {/* Modal Close Button */}
                    <button
                        onClick={onClose}
                        className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                        title="Close Standard Mappings"
                    >
                        <span className="material-symbols-outlined text-[22px]">close</span>
                    </button>
                </div>

                {/* Entity Navigation Tabs */}
                <div className="px-6 py-2.5 bg-slate-800/80 border-b border-slate-750 flex items-center gap-1.5 overflow-x-auto shrink-0">
                    <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mr-2 shrink-0">
                        Entities:
                    </span>
                    {canonicalEntities.map(ent => {
                        const isSelected = selectedEntityType.toLowerCase() === ent.toLowerCase();
                        const hasPreset = mappings.some(m => {
                            const norm = normalizeEntityName(m.TargetQBEntity || m.EntityType);
                            return norm.toLowerCase() === ent.toLowerCase();
                        });
                        const entMeta = getQbEntityMeta(ent);

                        return (
                            <button
                                key={ent}
                                onClick={() => handleSelectEntity(ent)}
                                className={`px-3 py-1 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1.5 shrink-0 ${
                                    isSelected
                                        ? 'bg-indigo-600 text-white shadow-sm'
                                        : 'bg-slate-900/80 text-slate-400 hover:text-white hover:bg-slate-750'
                                }`}
                            >
                                <span className="material-symbols-outlined text-[14px]">
                                    {entMeta.icon || 'table_chart'}
                                </span>
                                {hasPreset && (
                                    <span className={`w-1.5 h-1.5 rounded-full ${isSelected ? 'bg-emerald-300' : 'bg-emerald-400'}`}></span>
                                )}
                                {ent}
                            </button>
                        );
                    })}
                </div>

                {/* Notification Message */}
                {message && (
                    <div className={`px-6 py-2 text-xs border-b flex items-center justify-between gap-2 shrink-0 ${
                        message.type === 'success' ? 'bg-emerald-950/60 border-emerald-800 text-emerald-300' :
                        message.type === 'info' ? 'bg-blue-950/60 border-blue-800 text-blue-300' :
                        'bg-red-950/60 border-red-800 text-red-300'
                    }`}>
                        <div className="flex items-center gap-2">
                            <span className="material-symbols-outlined text-[16px]">
                                {message.type === 'success' ? 'check_circle' : message.type === 'info' ? 'info' : 'error'}
                            </span>
                            <span>{message.text}</span>
                        </div>
                        <button onClick={() => setMessage(null)} className="text-slate-400 hover:text-white text-xs">
                            &times;
                        </button>
                    </div>
                )}

                {/* Body Content using Unified FieldMappingDesigner */}
                <div className="flex-1 overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-full text-slate-400 text-xs">
                            <span className="material-symbols-outlined animate-spin text-2xl mr-2 text-indigo-400">sync</span>
                            Loading standard mapping preset...
                        </div>
                    ) : (
                        <FieldMappingDesigner
                            key={`${selectedSet}_${selectedSystem}_${selectedTargetSystem}_${selectedEntityType}_${selectedMapping?.UpdatedAt || selectedMapping?.MappingID || 'preset'}`}
                            mode="standard"
                            token={token}
                            standardMapping={activeMappingProp}
                            sourceSystem={selectedSystem}
                            onSave={handleSavePreset}
                            onResetDefaults={handleResetDefaults}
                            onClose={onClose}
                        />
                    )}
                </div>
            </div>

            {/* Create New Standard Set Modal */}
            {showNewSetModal && (
                <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/80 p-4">
                    <div className="bg-slate-900 border border-slate-700 rounded-xl p-6 w-full max-w-md shadow-2xl space-y-4">
                        <div className="flex justify-between items-center border-b border-slate-800 pb-3">
                            <h3 className="text-base font-bold text-white flex items-center gap-2">
                                <span className="material-symbols-outlined text-amber-400">add_circle</span>
                                Create Standard Set
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowNewSetModal(false)}
                                className="text-slate-400 hover:text-white text-lg"
                            >
                                &times;
                            </button>
                        </div>
                        <form onSubmit={handleCreateNewSet} className="space-y-4">
                            <div>
                                <label className="block text-xs font-medium text-slate-300 mb-1">Set Name *</label>
                                <input
                                    type="text"
                                    value={newSetName}
                                    onChange={e => setNewSetName(e.target.value)}
                                    placeholder="e.g. Retail Standard, Wholesale Profile, Xero Standard"
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                                    required
                                    autoFocus
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-300 mb-1">Clone from Existing Set</label>
                                <select
                                    value={cloneFromSet}
                                    onChange={e => setCloneFromSet(e.target.value)}
                                    className="w-full bg-slate-800 border border-slate-700 rounded px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-500"
                                >
                                    <option value="__NONE__">-- Start Blank (No Clone) --</option>
                                    {availableSets.map(s => (
                                        <option key={s} value={s}>{s}</option>
                                    ))}
                                </select>
                                <p className="text-[11px] text-slate-500 mt-1">
                                    Optionally copies all entity field mappings from an existing standard set into the new set.
                                </p>
                            </div>
                            <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                                <button
                                    type="button"
                                    onClick={() => setShowNewSetModal(false)}
                                    className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-xs"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    disabled={saving || !newSetName.trim()}
                                    className="px-4 py-1.5 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white rounded text-xs font-semibold transition-colors"
                                >
                                    {saving ? 'Creating...' : 'Create Set'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default StandardMappingsModal;
